import { useEffect, useState } from 'react'
import { Button, Input, Space } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import { useUpdateAdvisoryCallMutation } from '@/features/advisory-ai/hooks'

const { TextArea } = Input

type ReportAdvisoryNotesSectionProps = {
  callId: string
  clientId: string
  notes: string | null | undefined
}

export function ReportAdvisoryNotesSection({ callId, clientId, notes }: ReportAdvisoryNotesSectionProps) {
  const { addToast } = useToast()
  const updateCallMutation = useUpdateAdvisoryCallMutation(callId, clientId)
  const [isEditingNotes, setIsEditingNotes] = useState(false)
  const [advisorNotes, setAdvisorNotes] = useState(notes || '')
  const [editingNotesText, setEditingNotesText] = useState(notes || '')

  useEffect(() => {
    const nextNotes = notes || ''
    setAdvisorNotes(nextNotes)
    setEditingNotesText(nextNotes)
  }, [notes])

  const handleSaveNotes = async () => {
    try {
      await updateCallMutation.mutateAsync({ notes: editingNotesText })
      setAdvisorNotes(editingNotesText)
      setIsEditingNotes(false)
      addToast('Advisor notes updated successfully', 'success')
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to update advisor notes', 'error')
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-gray-900">Advisor Notes</h3>
        {!isEditingNotes ? (
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingNotesText(advisorNotes)
              setIsEditingNotes(true)
            }}
          />
        ) : (
          <Space>
            <Button
              onClick={() => {
                setEditingNotesText(advisorNotes)
                setIsEditingNotes(false)
              }}
            >
              Cancel
            </Button>
            <Button type="primary" loading={updateCallMutation.isPending} onClick={() => void handleSaveNotes()}>
              Save
            </Button>
          </Space>
        )}
      </div>
      {isEditingNotes ? (
        <TextArea
          value={editingNotesText}
          onChange={(e) => setEditingNotesText(e.target.value)}
          rows={6}
          className="w-full"
          placeholder="Add your notes here..."
        />
      ) : (
        <p className="text-gray-700 whitespace-pre-wrap min-h-[100px]">
          {advisorNotes || <span className="text-gray-400">No notes added yet.</span>}
        </p>
      )}
    </div>
  )
}
